import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack, Chip, Tabs, Tab } from "@mui/material";
import { CheckCircleOutlined, CancelOutlined } from "@mui/icons-material";
import CustomLayout from "../../../../components/layout";
import PrimaryButton from "../../../../components/buttons/primary-button";
import SecondaryButton from "../../../../components/buttons/secondary-button";
import LoanInfoCard from "../../../../components/info-card";
import QualifyAccordion, { inputStepStatus } from "../../../../components/qualify-accordion";
import CustomAlert from "../../../../components/custom-alert";
import { TabPanel } from "../../../../components/tab-panel";
import EmptyTextContainer from "../../../../components/empty-text-container";
import OutlinedTextInput from "../../../../components/custom-input/outlined-text-input";
import RequiredError from "../../../../components/required-error";
import LoaderModal from "../../../../components/modal/loader-modal";
import ProcessingModal from "../../../../components/modal/processing-modal";
import CommanErrorModal from "../../../../components/modal/common-error-modal";
import ConfirmationModal from "../../../../components/modal/confirmation-modal";
import CreditCredentialsInformation from "../../../../components/loan-form-card/credit-credentials-information";
import ExceptionRequestModal from "../../../../components/modal/exception-request-modal";
import Heading5 from "../../../../components/typography/heading-05";
import Body2 from "../../../../components/typography/body-02";
import Body3 from "../../../../components/typography/body-03";
import {
	setFicoScore,
	setAuraResult,
	setSelectedProgram,
	setCurrentStage,
	selectCurrentStage,
	setQualifyInputs,
	setLoanGuid,
	selectFicoScore,
	selectLiabilitiesChangedSinceAura,
	selectAuraResult,
	selectSectionStatuses,
	selectSelectedProgram,
	selectLoanGuid,
	selectLoanNumber,
	selectQualifyInputs,
	selectXmlApplicationData,
	setExceptionStatus,
	setIncomeDocumentationType,
	selectIncomeDocumentationType,
} from "../../../../features/current-loan/currentLoanSlice";
import { useSyncAuraProgress } from "../../../../features/current-loan/useSyncAuraProgress";
import { useProductInputsForm } from "./useProductInputsForm";
import ProductInputsPanel from "./ProductInputsPanel";
import {
	selectUserDetails,
	selectSelectedOrganization,
} from "../../../../features/auth/authSlice";
import { useCreateLoanMutation } from "../../../../features/create-loan/createLoanApiSlice";
import { useGetBorrowerPairMutation } from "../../../../features/get-borrower-pair/getBorrowerPairApiSlice";
import {
	useCreditVendorsMutation,
	useCreditVendorPublicKeyMutation,
} from "../../../../features/credit-reissue/creditVendorsApiSlice";
import { encryptCreditVendorPassword } from "../../../../utils/encrypt-credit-vendor-password";
import { isValidatedCreditReissueForm } from "../../../../utils/required-field-helper/credit-reissue-validation-helper";
import { VERBIAGE_CONSTANTS } from "../../../../utils/verbiage.constants";
import {
	REISSUE_STATUS,
	allPairsReissued,
	buildCreditReissueFieldValues,
	creditProvidedReissueField,
	needsCreditProvidedReissue,
	nextPairToReissue,
	resizeCreditCredentials,
	seedCreditCredentials,
	toLoanApplicationPeople,
	toPersistableCredentials,
} from "../../../../utils/aura-credit-reissue";
import { useCreateOrderServiceMutation } from "../../../../features/credit-reissue/createOrderServiceApiSlice";
import { useCheckOrderStatusMutation } from "../../../../features/credit-reissue/checkOrderStatusApiSlice";
import { usePersistReissueXmlCdoMutation } from "../../../../features/credit-reissue/reissueXmlCdoApiSlice";
import { buildReissueXmlCdoPayload } from "../../../../utils/buildReissueXmlCdoPayload";
import { useGetLoanMutation } from "../../../../features/get-loan-data/getLoanApiSlice";
import { useFieldMutation } from "../../../../features/field-values/fieldValueApiSlice";
import { useGetEligibleProductsMutation } from "../../../../features/get-eligible-products/getEligibleProductsApiSlice";
import { useGetEligibleProductDetailsMutation } from "../../../../features/get-eligible-product-details/getEligibleProductDetailsApiSlice";
import EligibleFilter from "../../../../components/loan-form-card/eligible-filter";
import { selectImpersonatedUser } from "../../../../features/admin/impersonated-user/impersonatedUserSlice";
import { selectedUser, selectedUserWithLoanChannel } from "../../../../utils/common-helpers";
import { normalizeXmlForCreateLoan, isRealLoanGuid } from "../../../../utils/normalize-xml-for-create-loan";
import { mapPersonFromReal } from "../../../../utils/current-loan-encompass-mappers";
import { useUpdateFieldsMutation } from "../../../../features/update-fields/updateFieldsApiSlice";
import {
	useRunAuraMutation,
	useLazyGetAuraStatusQuery,
	useLazyGetAuraConditionsQuery,
	useLazyGetRulesEngineStatusQuery,
} from "../../../../features/current-loan/currentLoanApiSlice";
import { useRegisterObProgram } from "../../../../features/current-loan/useRegisterObProgram";
import { useDownloadAttachmentMutation } from "../../../../features/download-attachment/downloadAttachmentApiSlice";
import {
	auraRunErrorKey,
	buildObRegistrationKey,
	formatElapsed,
	isAuraStatusDone,
	isGatewayOrNetworkError,
	normalizeAuraResult,
	pollRulesEngineUntilSettled,
	pollUntil,
} from "../../../../utils/aura-status-poll";
import { logRumEvent } from "../../../../utils/rum";
import RegisteredProgramCard from "../../../../components/registered-program-card";
import AuraConditionList from "../../../../components/aura-condition-list";
import AuraEligibilityMessages from "../../../../components/aura-eligibility-messages";
import AuraProgress from "../../../../components/aura-progress";
import { isDSCRstyle } from "../../../../utils/income-documentation-helpers";
import { getPostQualifyStage } from "../../../../utils/qualify-entry-path";
import { useErrorMessages } from "../../../../utils/error-messages.constants";
import { getConflictPopup, isConflictError, isObPushInQueue } from "../../../../utils/conflict-error";
import useAuraInProgress from "../../../../hooks/useAuraInProgress";
import AuraInProgressBanner from "../../../../components/aura-in-progress-banner";
import PageErrorBoundary from "../../../../components/page-error-boundary";
import styles from "./index.module.scss";
import {
	ENCOMPASS_FIELD_IDS,
	OCCUPANCY_LABELS,
	PROPERTY_TYPE_LABELS,
	PRODUCT_INPUTS_FIELD_IDS,
	MAX_CREDIT_STATUS_POLL_ATTEMPTS,
	AURA_POLL_INTERVAL_MS,
	AURA_POLL_TIMEOUT_MS,
} from "../../../../utils/aura.constants";
import {
	buildEligibleSearchFormData,
	buildProductInputsSaveValues,
	mapProductInputsFromFields,
} from "../../../../utils/product-inputs-encompass";

const formatDob = (raw) => {
	if (!raw) return "";
	const d = new Date(raw);
	if (isNaN(d.getTime())) return raw;
	const mm = String(d.getMonth() + 1).padStart(2, "0");
	const dd = String(d.getDate()).padStart(2, "0");
	return `${mm}/${dd}/${d.getFullYear()}`;
};

const maskSsn = (raw) => {
	if (!raw) return "";
	const digits = raw.replace(/\D/g, "");
	return digits.length >= 4 ? `XXX-XX-${digits.slice(-4)}` : "XXX-XX-XXXX";
};

const fmtAmount = (val) => {
	const n = Number(val);
	return !val || isNaN(n) ? "" : n.toLocaleString();
};


const ReadOnlyField = ({ label, value, tag }) => (
	<Stack direction="column" gap={0.5} className={styles.roField}>
		<Stack direction="row" alignItems="center" gap={1}>
			<Body2 text={label} fontType="semibold" />
			{tag && (
				<Chip
					label={tag}
					size="small"
					className={
						tag === "XML"
							? styles.tagXml
							: tag === "Credit re-issue"
							? styles.tagCredit
							: styles.tagCalc
					}
				/>
			)}
		</Stack>
		<OutlinedTextInput
			value={value || ""}
			disabled
			fullWidth
		/>
	</Stack>
);

// ── Panels ───────────────────────────────────────────────────────────────────
// Column layout (circle → label → sub, connecting line drawn behind each
// circle to the next) mirrors the CLEARpath prototype's .astep-wrap grid —
// same theme colors as before, just the same step-rail structure as the HTML.
const StepLabel = ({ letter, label, sub, status, isLast }) => (
	<Box className={styles.stepNode}>
		{!isLast && (
			<Box className={`${styles.stepLine} ${status === "done" ? styles.stepLineDone : ""}`} />
		)}
		<Stack direction="column" alignItems="center" gap={0.5}>
			<Box
				className={`${styles.stepCircle} ${
					status === "done"
						? styles.stepDone
						: status === "active"
						? styles.stepActive
						: styles.stepPending
				}`}
			>
				{status === "done" ? <CheckCircleOutlined sx={{ fontSize: 14 }} /> : letter}
			</Box>
			<span className={styles.stepLabel}>{label}</span>
			{sub && <span className={styles.stepSub}>{sub}</span>}
		</Stack>
	</Box>
);

// ── Main component ────────────────────────────────────────────────────────────
const QualifyAuraPage = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const loanGuid = useSelector(selectLoanGuid);
	useSyncAuraProgress();
	const currentStage = useSelector(selectCurrentStage);

	// Assert "qualify" on mount regardless of navigation method — previously only
	// path-select's "Begin qualification" button set this, so entering this page any
	// other way (e.g. the sidebar's own Qualify link once a path is already selected,
	// which routes straight here) left `currentStage` stuck at whatever it was before,
	// making the sidebar under-represent the current stage. Mirrors the same fix in
	// dashboard/index.jsx.
	useEffect(() => {
		if (loanGuid && currentStage !== "qualify") dispatch(setCurrentStage("qualify"));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid]);

	const loanNumber = useSelector(selectLoanNumber);
	const selectedProgram = useSelector(selectSelectedProgram);
	const savedFico = useSelector(selectFicoScore);
	const liabilitiesChangedSinceAura = useSelector(selectLiabilitiesChangedSinceAura);
	const savedAura = useSelector(selectAuraResult);
	const sectionStatuses = useSelector(selectSectionStatuses);
	const savedInputs = useSelector(selectQualifyInputs);
	const incomeDocumentationType = useSelector(selectIncomeDocumentationType);
	const xmlApplicationData = useSelector(selectXmlApplicationData);
	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const effectiveUser = selectedUser(userDetails, impersonatedUser);
	const selectedOrganization = useSelector(selectSelectedOrganization);

	const [createLoan] = useCreateLoanMutation();
	const [getBorrowerPair] = useGetBorrowerPairMutation();
	const [fetchCreditVendors] = useCreditVendorsMutation();
	const [createOrderService] = useCreateOrderServiceMutation();
	const [checkOrderStatus] = useCheckOrderStatusMutation();
	const [getCreditVendorPublicKey] = useCreditVendorPublicKeyMutation();
	const [persistReissueXmlCdo] = usePersistReissueXmlCdoMutation();
	const [getLoan] = useGetLoanMutation();
	const [field] = useFieldMutation();
	const [getEligibleProducts] = useGetEligibleProductsMutation();
	const [getEligibleProductDetails] = useGetEligibleProductDetailsMutation();
	const [updateFields] = useUpdateFieldsMutation();
	const [runAura] = useRunAuraMutation();
	const [getAuraStatus] = useLazyGetAuraStatusQuery();
	const [getAuraConditions] = useLazyGetAuraConditionsQuery();
	const [getRulesEngineStatus] = useLazyGetRulesEngineStatusQuery();
	const [downloadAttachment] = useDownloadAttachmentMutation();
	const registerObProgram = useRegisterObProgram();

	const pollTimerRef = useRef(null);
	const isMountedRef = useRef(true);
	const pollAttemptsRef = useRef(0);
	const lastPollArgsRef = useRef(null);
	const [pollTimedOut, setPollTimedOut] = useState(false);

	// Values already saved for this loan (Dashboard, or an earlier Qualify visit) —
	// fetched fresh on mount so this page shows the same data Dashboard does, instead
	// of relying solely on the (session-only, often-already-cleared) uploaded XML.
	const [backendData, setBackendData] = useState({});
	// Encompass borrower pairs (getBorrowerPair response) — drives one credit re-issue
	// tab per pair in Step A, like the Add New Loan wizard.
	const [borrowerPairs, setBorrowerPairs] = useState([]);

	useEffect(() => {
		if (!isRealLoanGuid(loanGuid)) return;
		getBorrowerPair({ loanGuid })
			.unwrap()
			.then((data) => {
				const list = data?.response || data;
				if (Array.isArray(list)) setBorrowerPairs(list);
				const pair = list?.[0];
				if (!pair?.borrower) return;
				const mapped = mapPersonFromReal(pair.borrower);
				setBackendData((prev) => ({
					...prev,
					firstName: mapped?.borrower?.firstName || prev.firstName,
					lastName: mapped?.borrower?.lastName || prev.lastName,
					dateOfBirth: formatDob(mapped?.borrower?.dob) || prev.dateOfBirth,
					ssn: mapped?.borrower?.ssn || prev.ssn,
				}));
			})
			.catch(() => {});
		getLoan({ loanid: loanGuid })
			.unwrap()
			.then((loanData) => {
				const property = loanData?.response?.property;
				if (!property) return;
				setBackendData((prev) => ({
					...prev,
					propertyType: property.propertyType || prev.propertyType,
					occupancy: property.occupancy || prev.occupancy,
					state: property.state || prev.state,
					county: property.county || prev.county,
					city: property.city || prev.city,
					zip: property.zip || prev.zip,
				}));
			})
			.catch(() => {});
	}, [loanGuid]);

	// Panel A — status + open derived from Redux so returning to the page restores state
	const [panelAOpen, setPanelAOpen] = useState(!savedFico);
	const [panelAStatus, setPanelAStatus] = useState(savedFico ? "complete" : "active");
	// One credentials form per borrower pair (wizard shape, DEFAULT_CREDIT_CREDENTIALS_FORM).
	// Passwords/saved-vendor secrets are never persisted — see toPersistableCredentials.
	const [creditCredentialsData, setCreditCredentialsData] = useState(() =>
		seedCreditCredentials(savedInputs)
	);
	const [reissueStatuses, setReissueStatuses] = useState([]);
	const reissueStatusesRef = useRef([]);
	const [creditTab, setCreditTab] = useState(0);
	const [creditErrorPair, setCreditErrorPair] = useState(-1);
	const [reissueDisclaimerOpen, setReissueDisclaimerOpen] = useState(false);
	const [creditVendorsData, setCreditVendorsData] = useState([]);
	const [panelAError, setPanelAError] = useState("");
	const [creditResult, setCreditResult] = useState(
		savedFico ? { fico: savedFico, tradelines: "—", housing: "—", event: "—" } : null
	);
	const [runningCredit, setRunningCredit] = useState(false);
	// PathA-2.02 / 2.17: pair awaiting the pre-re-issue confirmation (null = none).
	const [reissueConfirmPair, setReissueConfirmPair] = useState(null);
	// Pairs already re-issued on this loan (CX.LC.CreditVendor.PairN set) — repeat warning.
	const [pairsReissuedOnLoan, setPairsReissuedOnLoan] = useState(new Set());
	// PathA-2.04: post-re-issue prompt after the last pair completes.
	const [reissueCompleteOpen, setReissueCompleteOpen] = useState(false);
	// PathA-3.04: Step B DTI read from the saved loan.
	const [readingDti, setReadingDti] = useState(false);

	// Panel B
	const [panelBOpen, setPanelBOpen] = useState(!!savedFico && !savedAura);
	const [panelBStatus, setPanelBStatus] = useState(
		savedAura ? "complete" : savedFico ? "active" : "pending"
	);
	const [panelBError, setPanelBError] = useState("");
	const [searchingPrograms, setSearchingPrograms] = useState(false);

	// Panel C
	const [panelCOpen, setPanelCOpen] = useState(false);
	const [panelCStatus, setPanelCStatus] = useState(savedAura ? "complete" : "pending");
	const [runningAura, setRunningAura] = useState(false);
	const [eligibleProducts, setEligibleProducts] = useState({});
	const [expandedProduct, setExpandedProduct] = useState("");
	const [eligibleProductDetails, setEligibleProductDetails] = useState({});
	const [selectedRow, setSelectedRow] = useState({});
	const [selectedProduct, setSelectedProduct] = useState("");
	const [selectedRowProductId, setSelectedRowProductId] = useState("");
	const [processingText, setProcessingText] = useState("Running AURA…");

	// Panel D — "registered" (not lockDecision, which is now only ever set later on
	// Submit's pre-submission step) is the completion signal for this panel.
	const [panelDOpen, setPanelDOpen] = useState(!!savedAura && !savedInputs.registered);
	const [panelDStatus, setPanelDStatus] = useState(
		savedInputs.registered ? "complete" : savedAura ? "active" : "pending"
	);
	const [auraStatus, setAuraStatus] = useState(savedAura || null);
	const [auraRawResult, setAuraRawResult] = useState("");
	const [auraFindings, setAuraFindings] = useState(null);
	const [auraElapsed, setAuraElapsed] = useState("");
	const [auraPending, setAuraPending] = useState(false);
	const [auraNotConfigured, setAuraNotConfigured] = useState(false);
	// LC-696: "AURA is evaluating your loan" — from the run until the conditions settle.
	const [auraEvaluating, setAuraEvaluating] = useState(false);
	const [auraConditions, setAuraConditions] = useState(null);
	// Step 8 didn't finish within its limit (LC-AURA-005): conditions may still be loading.
	const [rulesEngineIncomplete, setRulesEngineIncomplete] = useState(false);
	const [refreshingConditions, setRefreshingConditions] = useState(false);
	const [exceptionModalOpen, setExceptionModalOpen] = useState(false);
	const [commonErrorModalVisible, setCommonErrorModalVisible] = useState(false);
	const [commonErrorData, setCommonErrorData] = useState(null);

	const { getErrorMessage } = useErrorMessages();

	const handleOpenCommonErrorModal = (errorKey) => {
		setCommonErrorData(getErrorMessage(errorKey));
		setCommonErrorModalVisible(true);
	};
	const handleCloseCommonErrorModal = () => setCommonErrorModalVisible(false);
	// 409 → popup with the backend's message ("AURA is running" / "Loan was changed").
	const showConflictPopup = (err) => {
		if (!isConflictError(err)) return false;
		setCommonErrorData(getConflictPopup(err, getErrorMessage));
		setCommonErrorModalVisible(true);
		return true;
	};
	// Edits are locked while an AURA run is in flight. This page's own run (Run/Retry →
	// polling) keeps today's Step C/D UI; the lock covers a run started elsewhere.
	const { locked: auraLocked, recheck: recheckAuraLock } = useAuraInProgress(loanGuid);
	const editLocked = auraLocked && !runningAura && !auraEvaluating;
	// When this page's own run settles, re-read the lock right away instead of waiting a poll.
	useEffect(() => {
		if (!auraEvaluating && auraLocked) recheckAuraLock();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [auraEvaluating]);

	useEffect(() => {
		if (!loanGuid) navigate("/current-loan");
	}, [loanGuid, navigate]);

	// Fetch credit vendors on mount (same API as credit-reissue-application)
	useEffect(() => {
		const loadVendors = async () => {
			try {
				const response = await fetchCreditVendors().unwrap();
				const filtered = (response || [])
					.filter((v) => v.group === "Group 1" || v.group === "Group 4")
					.sort((a, b) => a.partner_name.localeCompare(b.partner_name));
				setCreditVendorsData(filtered);
			} catch (_) {
				// non-blocking — form still usable without vendor list
			}
		};
		loadVendors();
	}, []);// eslint-disable-line react-hooks/exhaustive-deps

	// Borrower pairs for Step A: the Encompass pairs once the loan exists, otherwise the
	// uploaded XML's applications (the loan is created on the first re-issue).
	const creditPairs = borrowerPairs.length
		? borrowerPairs
		: xmlApplicationData?.applications || [];
	const creditPairPeople = toLoanApplicationPeople(creditPairs);
	const creditPairCount = Math.max(1, creditPairPeople.length / 2);

	// One credentials form per pair — grows/shrinks with the pair count, keeping entries.
	useEffect(() => {
		setCreditCredentialsData((prev) =>
			prev.length === creditPairCount ? prev : resizeCreditCredentials(prev, creditPairCount)
		);
	}, [creditPairCount]);

	// Persist non-secret credential fields so returning to the page restores them.
	useEffect(() => {
		dispatch(setQualifyInputs({ creditCredentials: toPersistableCredentials(creditCredentialsData) }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [creditCredentialsData]);

	// Clear polling timer on unmount and mark component as unmounted. Reset to
	// true on (re)mount too — React.StrictMode dev double-invokes this effect
	// (mount → cleanup → mount) before the component is actually in use, which
	// would otherwise leave isMountedRef stuck at false and silently kill the
	// credit-status poll's setTimeout chain after its first tick.
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
		};
	}, []);

	const xmlData = React.useMemo(() => {
		if (!xmlApplicationData) return {};
		const app = xmlApplicationData?.applications?.[0] || {};
		const b = app?.borrower || {};
		const prop = xmlApplicationData?.property || {};
		const loanAmountRaw = Number(xmlApplicationData?.borrowerRequestedLoanAmount || app?.loanAmount || 0);
		const purchasePriceRaw = Number(xmlApplicationData?.purchasePriceAmount || app?.purchasePriceAmount || 0);
		const appraisedValueRaw = Number(prop?.appraisedValue || prop?.estimatedValue || purchasePriceRaw || 0);
		return {
			firstName: b?.firstName || "",
			lastName: b?.lastName || "",
			dateOfBirth: formatDob(b?.birthDate),
			ssn: maskSsn(b?.taxIdentificationIdentifier),
			loanPurpose: app?.loanPurposeType || "",
			loanAmount: fmtAmount(loanAmountRaw),
			purchasePrice: fmtAmount(purchasePriceRaw),
			appraisedValue: fmtAmount(appraisedValueRaw),
			propertyType: PROPERTY_TYPE_LABELS[prop?.gsePropertyType || prop?.propertyType] || prop?.gsePropertyType || prop?.propertyType || "",
			occupancy: OCCUPANCY_LABELS[app?.propertyUsageType] || app?.propertyUsageType || "",
			state: prop?.state || "",
			county: prop?.county || "",
			city: prop?.city || "",
			zip: prop?.postalCode || "",
			loanAmountRaw,
			appraisedValueRaw,
		};
	}, [xmlApplicationData]);

	// When live XML data is present, persist the display fields to Redux (localStorage-backed)
	// so they are available when the user returns after navigating to the dashboard.
	React.useEffect(() => {
		if (!xmlData || !xmlData.firstName) return;
		dispatch(setQualifyInputs({
			xmlFirstName: xmlData.firstName,
			xmlLastName: xmlData.lastName,
			xmlDob: xmlData.dateOfBirth,
			xmlSsn: xmlData.ssn,
			xmlLoanPurpose: xmlData.loanPurpose,
			xmlLoanAmount: xmlData.loanAmount,
			xmlPurchasePrice: xmlData.purchasePrice,
			xmlAppraisedValue: xmlData.appraisedValue,
			xmlPropertyType: xmlData.propertyType,
			xmlOccupancy: xmlData.occupancy,
			xmlState: xmlData.state,
			xmlCounty: xmlData.county,
			xmlCity: xmlData.city,
			xmlZip: xmlData.zip,
			xmlLoanAmountRaw: String(xmlData.loanAmountRaw || 0),
			xmlAppraisedValueRaw: String(xmlData.appraisedValueRaw || 0),
		}));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [xmlData]);

	// Merged display data — real saved data (Dashboard/backend) takes priority, then
	// live XML from this session, then whatever was persisted to Redux from an earlier
	// visit. Order matters: backendData is the only one that reflects what's actually
	// saved, so it must win over the (often already-cleared) session-only XML.
	const displayData = {
		firstName: backendData.firstName || xmlData.firstName || savedInputs.xmlFirstName || "",
		lastName: backendData.lastName || xmlData.lastName || savedInputs.xmlLastName || "",
		dateOfBirth: backendData.dateOfBirth || xmlData.dateOfBirth || savedInputs.xmlDob || "",
		ssn: backendData.ssn || xmlData.ssn || savedInputs.xmlSsn || "",
		loanPurpose: xmlData.loanPurpose || savedInputs.xmlLoanPurpose || "",
		loanAmount: xmlData.loanAmount || savedInputs.xmlLoanAmount || "",
		purchasePrice: xmlData.purchasePrice || savedInputs.xmlPurchasePrice || "",
		appraisedValue: xmlData.appraisedValue || savedInputs.xmlAppraisedValue || "",
		propertyType: backendData.propertyType || xmlData.propertyType || savedInputs.xmlPropertyType || "",
		occupancy: backendData.occupancy || xmlData.occupancy || savedInputs.xmlOccupancy || "",
		state: backendData.state || xmlData.state || savedInputs.xmlState || "",
		county: backendData.county || xmlData.county || savedInputs.xmlCounty || "",
		city: backendData.city || xmlData.city || savedInputs.xmlCity || "",
		zip: backendData.zip || xmlData.zip || savedInputs.xmlZip || "",
		loanAmountRaw: xmlData.loanAmountRaw || Number(savedInputs.xmlLoanAmountRaw) || 0,
		appraisedValueRaw: xmlData.appraisedValueRaw || Number(savedInputs.xmlAppraisedValueRaw) || 0,
	};

	// Product inputs form — Panel B full form (hook must be after displayData + creditResult)
	const {
		formState: piFormState,
		setField: piSetField,
		hydrateFromEncompass: piHydrateFromEncompass,
		errors: piErrors,
		setErrors: piSetErrors,
		submitted: piSubmitted,
		setSubmitted: piSetSubmitted,
		validateForm: piValidateForm,
	} = useProductInputsForm({ displayData, creditResult });

	// Fetch-on-open: whenever Panel B opens, re-fetch Panel B's fields from Encompass and
	// merge them over local state — mirrors Dashboard's refetch-on-section-open. Gated on
	// panelBOpen transitioning false→true (not just "is open") so it doesn't re-fire on
	// every render, and only after loanGuid is real (a brand-new loan has nothing to fetch
	// yet — the XML/credit-result seeding effects in useProductInputsForm already cover
	// that case).
	const panelBOpenForFetchRef = useRef(false);
	useEffect(() => {
		if (!panelBOpenForFetchRef.current && panelBOpen && isRealLoanGuid(loanGuid)) {
			field({ loan_guid: loanGuid, fields: PRODUCT_INPUTS_FIELD_IDS.join(",") })
				.unwrap()
				.then((res) => {
					const hydrated = mapProductInputsFromFields(res?.response);
					piHydrateFromEncompass(hydrated);
					// Income Verification Type is read-only here and mirrors the Dashboard's
					// value — when that's still blank, adopt the saved Encompass value so the
					// mirror effect doesn't leave the field empty.
					if (!incomeDocumentationType && hydrated.incomeVerificationType) {
						dispatch(setIncomeDocumentationType(hydrated.incomeVerificationType));
					}
				})
				.catch(() => {
					// silent — best-effort refresh, local/XML-seeded values stay as-is on failure
				});
		}
		panelBOpenForFetchRef.current = panelBOpen;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [panelBOpen, loanGuid]);

	// Step B entry: DTI is Encompass's calculation on the saved loan (field 742), read-only
	// (RF-34 / PathA-3.04). DSCR loans don't show DTI, so nothing is read for them.
	// Read once per opening of Step B, but only counted as done once the loan guid and
	// income type allow the read — a Step B opened while those are still loading is
	// retried when they arrive instead of staying blank. Closing Step B re-arms it, so
	// reopening after Dashboard edits re-reads.
	// Reads DTI (742) from Encompass into the form; resolves the value ("" when not calculated).
	const readEncompassDti = async () => {
		const res = await field({ loan_guid: loanGuid, fields: ENCOMPASS_FIELD_IDS.DTI }).unwrap();
		const bottomRatio = (res?.response || []).find((f) => String(f?.fieldId) === ENCOMPASS_FIELD_IDS.DTI)?.value;
		const dti = bottomRatio ?? "";
		piHydrateFromEncompass({ debtToIncomeRatio: dti });
		return dti;
	};

	const dtiReadForOpenRef = useRef(false);
	useEffect(() => {
		if (!panelBOpen) {
			dtiReadForOpenRef.current = false;
			return;
		}
		if (dtiReadForOpenRef.current) return;
		if (!isRealLoanGuid(loanGuid) || isDSCRstyle(incomeDocumentationType)) return;
		dtiReadForOpenRef.current = true;
		setReadingDti(true);
		readEncompassDti()
			.catch(() => {
				dtiReadForOpenRef.current = false;
				handleOpenCommonErrorModal("DTI_READ_ERROR");
			})
			.finally(() => setReadingDti(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [panelBOpen, loanGuid, incomeDocumentationType]);

	// Step A is complete — open Panel B. With no successful re-issue (broker continued
	// past the disclaimer), fall back to the manual-review FICO as before.
	const completePanelA = (knownFico) => {
		if (!knownFico && !creditResult) {
			// No re-issue: nothing is known, so nothing is shown as if it were (PathA-2.11).
			setCreditResult({ fico: "—", tradelines: "—", housing: "—", event: "—" });
		}
		setPanelAStatus("complete");
		setPanelAOpen(false);
		setPanelBStatus("active");
		setPanelBOpen(true);
		ensureCreditProvidedReissue();
	};

	// Dashboard Step 2 "How will credit be provided?" reads CX.LC.Credit.Provided. A pair
	// re-issued in an earlier visit (CX.LC.CreditVendor.PairN set) leaves it blank, so fill
	// it with Reissue — only when blank, and never while AURA runs.
	const ensureCreditProvidedReissue = async () => {
		if (editLocked || !isRealLoanGuid(loanGuid)) return;
		const hasReissuedPair =
			pairsReissuedOnLoan.size > 0 ||
			reissueStatusesRef.current.some((status) => status === REISSUE_STATUS.SUCCESS);
		if (!hasReissuedPair) return;
		const fieldId = creditProvidedReissueField().id;
		try {
			const res = await field({ loan_guid: loanGuid, fields: fieldId }).unwrap();
			const current = (res?.response || []).find(
				(f) => String(f?.fieldId || "").toUpperCase() === fieldId.toUpperCase()
			)?.value;
			if (!needsCreditProvidedReissue(current, hasReissuedPair)) return;
			await updateFields({ loan_guid: loanGuid, field_values: [creditProvidedReissueField()] }).unwrap();
		} catch (err) {
			// Background fill — the broker can still pick it on the Dashboard; a 409 is always shown.
			showConflictPopup(err);
		}
	};

	// Ref mirror so the async poll chain always sees the latest per-pair statuses.
	const setPairStatus = (pairIndex, status) => {
		const next = [...reissueStatusesRef.current];
		next[pairIndex] = status;
		reissueStatusesRef.current = next;
		setReissueStatuses(next);
		return next;
	};

	// One pair failed — same LC-CRED-001 popup as the wizard; the broker stays on
	// Step A to retry (or continue past the disclaimer).
	const failPairReissue = (pairIndex) => {
		setRunningCredit(false);
		pollAttemptsRef.current = 0;
		setPollTimedOut(false);
		setPairStatus(pairIndex, REISSUE_STATUS.FAILED);
		handleOpenCommonErrorModal("CREDIT_REISSUE_FAILED");
	};

	// The qualifying credit score isn't in checkOrderStatus's response — the vendor
	// writes it to Encompass, read back via VASUMM.X23 (as create-loan-terms does).
	const refreshQualifyingFico = async (guid, response) => {
		let fico;
		try {
			const fieldsResponse = await field({ loan_guid: guid, fields: ENCOMPASS_FIELD_IDS.QUALIFYING_FICO }).unwrap();
			fico = fieldsResponse?.response?.find((f) => f.fieldId === ENCOMPASS_FIELD_IDS.QUALIFYING_FICO)?.value;
		} catch (_) {
			// fall through — fico stays undefined, handled below
		}
		// Values the vendor didn't return show as "—" — never a placeholder number (PathA-2.11).
		setCreditResult({
			fico: fico || "—",
			tradelines: response?.tradelines ?? "—",
			housing: response?.housing_history || "—",
			event: response?.housing_event || "—",
		});
		if (fico) dispatch(setFicoScore(fico));
		return fico;
	};

	// Polls checkOrderStatus every 2s for one borrower pair (mirrors
	// credit-reissue-application checkReissueStatus), capped at
	// MAX_CREDIT_STATUS_POLL_ATTEMPTS so a vendor order stuck at "Pending" (common in
	// sandbox/test credit-vendor connections) doesn't spin forever.
	const checkReissueStatus = async (transactionId, pollArgs) => {
		const { pairIndex, guid, borrowerPairId, statusCredentials, form } = pollArgs;
		lastPollArgsRef.current = { transactionId, pollArgs };
		let response;
		try {
			response = await checkOrderStatus({ ...statusCredentials, transactionId }).unwrap();
		} catch (err) {
			failPairReissue(pairIndex);
			return;
		}

		const bodyStatus = response?.status?.toLowerCase();
		if (bodyStatus === "pending" || (!bodyStatus && !response?.errors?.length)) {
			pollAttemptsRef.current += 1;
			if (pollAttemptsRef.current >= MAX_CREDIT_STATUS_POLL_ATTEMPTS) {
				setRunningCredit(false);
				setPollTimedOut(true);
				return;
			}
			if (isMountedRef.current) {
				pollTimerRef.current = setTimeout(() => checkReissueStatus(transactionId, pollArgs), 2000);
			}
			return;
		}
		if (bodyStatus !== "success") {
			failPairReissue(pairIndex);
			return;
		}

		// The XML CDO save (LC-545, which also maintains LC~CreditReportIndex) is the
		// critical step — as in the wizard it's awaited and gates success.
		try {
			const xmlSaveResult = await persistReissueXmlCdo(
				buildReissueXmlCdoPayload({
					loanGuid: guid,
					borrowerPairId,
					creditReportIdentifier: form.creditReportIdentifier,
					reportOn: form.reportOn,
					transactionId,
					providerId: form.providerId,
					statusResponse: response,
				})
			).unwrap();
			if (!xmlSaveResult?.success) {
				failPairReissue(pairIndex);
				return;
			}
			// Same Encompass fields the wizard writes: 300#N and CX.LC.CreditVendor.PairN.
			await updateFields({
				loan_guid: guid,
				field_values: buildCreditReissueFieldValues(pairIndex, form),
			}).unwrap();
		} catch (err) {
			showConflictPopup(err);
			failPairReissue(pairIndex);
			return;
		}

		const fico = await refreshQualifyingFico(guid, response);
		pollAttemptsRef.current = 0;
		setPollTimedOut(false);
		setRunningCredit(false);
		setPanelAError("");
		const statuses = setPairStatus(pairIndex, REISSUE_STATUS.SUCCESS);
		setPairsReissuedOnLoan((prev) => new Set(prev).add(pairIndex));
		if (allPairsReissued(statuses, creditPairCount)) {
			completePanelA(fico);
			setReissueCompleteOpen(true);
		} else {
			const nextPair = nextPairToReissue(statuses, creditPairCount, pairIndex);
			if (nextPair >= 0) setCreditTab(nextPair);
		}
	};

	// Manually resumes polling after a timeout, for another MAX_CREDIT_STATUS_POLL_ATTEMPTS.
	const handleCheckCreditStatusAgain = () => {
		if (!lastPollArgsRef.current) return;
		pollAttemptsRef.current = 0;
		setPollTimedOut(false);
		setRunningCredit(true);
		const { transactionId, pollArgs } = lastPollArgsRef.current;
		checkReissueStatus(transactionId, pollArgs);
	};

	// Encompass loan GUID for the re-issue — creates the loan from the uploaded XML
	// the first time (same as before), then loads its borrower pairs.
	const ensureLoanAndPairs = async () => {
		let guid = loanGuid;
		if (!isRealLoanGuid(guid)) {
			if (!xmlApplicationData) {
				throw new Error("Session expired. Please re-upload your file to continue.");
			}
			const result = await createLoan({
				...normalizeXmlForCreateLoan(xmlApplicationData),
				tpo_user_id: effectiveUser?.user_tpo_id,
				selectedOrganization,
				userDetails: effectiveUser,
			}).unwrap();
			guid = result.GUID;
			dispatch(setLoanGuid({ loanGuid: result.GUID, loanNumber: result.Loan_Number }));
		}
		let pairs = borrowerPairs;
		if (!pairs.length || guid !== loanGuid) {
			const response = await getBorrowerPair({ loanGuid: guid }).unwrap();
			pairs = response?.response || response || [];
			setBorrowerPairs(pairs);
		}
		return { guid, pairs };
	};

	// A pair whose CX.LC.CreditVendor.PairN is already set was re-issued before (PathA-2.17).
	useEffect(() => {
		if (!isRealLoanGuid(loanGuid)) return;
		const ids = [1, 2, 3].map((n) => `CX.LC.CreditVendor.Pair${n}`);
		field({ loan_guid: loanGuid, fields: ids.join(",") })
			.unwrap()
			.then((res) => {
				const reissued = new Set();
				(res?.response || []).forEach((f) => {
					const index = ids.findIndex((id) => id.toUpperCase() === String(f?.fieldId || "").toUpperCase());
					if (index >= 0 && f?.value) reissued.add(index);
				});
				setPairsReissuedOnLoan((prev) => new Set([...prev, ...reissued]));
			})
			.catch(() => {
				// best-effort — without it only this session's re-issues trigger the repeat warning
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid]);

	const isRepeatReissue = (pairIndex) =>
		reissueStatuses[pairIndex] === REISSUE_STATUS.SUCCESS || pairsReissuedOnLoan.has(pairIndex);

	// "Review liabilities on Assets & liabilities" — deep-link into the Dashboard section.
	const handleReviewLiabilities = () => {
		setReissueCompleteOpen(false);
		dispatch(setCurrentStage("dashboard"));
		navigate("/current-loan/dashboard", { state: { openSection: "assets" } });
	};

	// Step A — re-issue credit for one borrower pair (wizard reissueCredit), using
	// that pair's own vendor credentials and an encrypted vendor password.
	const handleReissuePair = async (pairIndex) => {
		if (editLocked) return;
		const form = creditCredentialsData[pairIndex] || {};
		// isValidatedCreditReissueForm dereferences the vendor, so a saved vendor that
		// isn't in this page's vendor list (Group 1/4 only) is treated as invalid.
		const vendorListed = creditVendorsData.some((v) => v.partner_name === form.creditVendor);
		if (
			!vendorListed ||
			!isValidatedCreditReissueForm({
				creditCredentialsData,
				creditVendorsData,
				borrowerPairCount: creditPairCount,
				borrowerIndex: pairIndex,
			})
		) {
			setCreditErrorPair(pairIndex);
			setPanelAError(
				`Please provide a value for all credit credential fields highlighted above (borrower pair ${pairIndex + 1}).`
			);
			return;
		}
		setCreditErrorPair(-1);
		setPanelAError("");
		setRunningCredit(true);
		pollAttemptsRef.current = 0;
		setPollTimedOut(false);
		setPairStatus(pairIndex, REISSUE_STATUS.PENDING);

		let guid;
		let borrowerPairId;
		try {
			const loan = await ensureLoanAndPairs();
			guid = loan.guid;
			borrowerPairId = loan.pairs?.[pairIndex]?.borrowerPairId || "";
		} catch (err) {
			setRunningCredit(false);
			if (showConflictPopup(err)) return;
			setPanelAError(err?.data?.detail || err?.message || "Could not prepare the loan for credit re-issue.");
			return;
		}
		if (!borrowerPairId) {
			setRunningCredit(false);
			setPanelAError(`Borrower pair ${pairIndex + 1} was not found on this loan in Encompass.`);
			return;
		}

		const { password, encryptedPassword, prepopulatedSubscriberId, ...credentials } = form;
		const statusCredentials = {
			...credentials,
			subscriberId: prepopulatedSubscriberId || credentials.subscriberId,
		};
		try {
			let encryptedPasswordForRequest = encryptedPassword;
			if (!encryptedPasswordForRequest) {
				const { public_key } = await getCreditVendorPublicKey().unwrap();
				encryptedPasswordForRequest = await encryptCreditVendorPassword(password, public_key);
			}
			const orderResponse = await createOrderService({
				...statusCredentials,
				encrypted_password: encryptedPasswordForRequest,
				entityId: `${guid}#${borrowerPairId}`,
				entityType: "urn:elli:encompass:loan:borrower",
			}).unwrap();
			if (!orderResponse?.transaction_id) {
				failPairReissue(pairIndex);
				return;
			}
			pollTimerRef.current = setTimeout(
				() =>
					checkReissueStatus(orderResponse.transaction_id, {
						pairIndex,
						guid,
						borrowerPairId,
						statusCredentials,
						form,
					}),
				0
			);
		} catch (err) {
			showConflictPopup(err);
			failPairReissue(pairIndex);
		}
	};

	// Continue past Step A — the wizard's disclaimer when any pair isn't re-issued.
	const handleContinueFromCredit = () => {
		if (allPairsReissued(reissueStatusesRef.current, creditPairCount)) {
			completePanelA();
			return;
		}
		setReissueDisclaimerOpen(true);
	};

	// Panel B submit — advances to Panel C even if the API fails
	const handleGetEligibleProductDetails = async (productId) => {
		try {
			const response = await getEligibleProductDetails({
				ob_business_channel_id: eligibleProducts?.data?.ob_business_channel_id,
				ob_originator_id: eligibleProducts?.data?.ob_originator_id,
				ob_loan_id: eligibleProducts?.data?.ob_loan_id,
				search_id: eligibleProducts?.data?.searchId,
				product_id: productId,
			}).unwrap();
			setEligibleProductDetails(response);
		} catch (_) {
			// non-blocking — "show all options" hydration failing shouldn't block the flow
		}
	};

	const handleToggleExpand = (productId) => {
		if (expandedProduct === productId) {
			setExpandedProduct("");
		} else {
			setExpandedProduct(productId);
			handleGetEligibleProductDetails(productId);
		}
	};

	// Minimum set of fields Optimal Blue needs to search programs at all — mirrors
	// product-inputs-required-helper.js's canSearchPrograms (loanAmount/ltv/propertyType/
	// occupancy/state/qualifyingFico/incomeDocType), adapted to where AURA actually keeps
	// each value: most of it lives in Redux productInputs (piFormState), but property/
	// occupancy/state are read-only fields sourced from the loan itself (displayData).
	const getMissingSearchFields = () => {
		const missing = [];
		if (!piFormState.loanAmount) missing.push("Loan Amount");
		if (!piFormState.qualifyingCreditScore) missing.push("Qualifying Credit Score (FICO)");
		if (!piFormState.incomeVerificationType) missing.push("Income Verification Type");
		if (!piFormState.propertyType) missing.push("Property Type");
		if (!piFormState.occupancy) missing.push("Occupancy");
		if (!piFormState.zipcode) missing.push("Zipcode");
		if (!displayData.state) missing.push("State");
		return missing;
	};

	// Writes Panel B's product-inputs fields to the real Encompass loan record. Called
	// both from handleSearchPrograms below (unchanged — still gated on validation, still
	// throws on failure so the search aborts with a visible error rather than running
	// against stale Encompass data) and from a save-on-leave effect (see the panelBOpen
	// effect further down) that fires regardless of validation, mirroring Dashboard's
	// autosaveSection "always persist on navigate, even if invalid" pattern — so partial
	// Panel B progress isn't lost if the broker navigates away before ever clicking
	// "Search programs." The save-on-leave caller is responsible for its own try/catch
	// (kept silent there, same as Dashboard's autosaveSection) — this function itself
	// does not swallow errors.
	const savePanelBProductInputs = async () => {
		if (!isRealLoanGuid(loanGuid)) return;
		await updateFields({
			loan_guid: loanGuid,
			field_values: buildProductInputsSaveValues(piFormState),
		}).unwrap();
	};

	const handleSearchPrograms = async () => {
		if (editLocked) return;
		piSetSubmitted(true);
		const piFormErrors = piValidateForm();
		// Actually highlight the offending fields (previously computed but never
		// applied, so the "highlighted above" banner text was never true — every
		// field's error prop always evaluated false and nothing turned red).
		piSetErrors(piFormErrors);

		if (Object.keys(piFormErrors).length > 0) {
			setPanelBError("Please fill in all required fields highlighted above.");
			return;
		}
		setPanelBError("");

		// Property/occupancy/state/loan amount/FICO/income type are required by Optimal
		// Blue itself — block here with a clear message instead of letting the API call
		// fail with a raw 400 (these fields have no confirmed real-time source to
		// auto-fill from here, so if the loan's own XML/backend data didn't provide them,
		// the user must fix the loan record before pricing can run).
		const missingSearchFields = getMissingSearchFields();
		if (missingSearchFields.length > 0) {
			setPanelBError(
				`Missing required field(s) for pricing search: ${missingSearchFields.join(", ")}. ` +
					`Please confirm these on the loan (property details, income type, credit score) before searching.`
			);
			return;
		}

		setSearchingPrograms(true);
		try {
			const effectiveUserWithChannel = selectedUserWithLoanChannel(
				userDetails,
				impersonatedUser,
				selectedOrganization
			);

			// OB reads almost everything (income type, DTI/DSCR inputs, loan purpose/amount,
			// credit profile, prepayment penalty, stories, etc.) straight off the Encompass
			// loan record rather than this request body — push the full Panel B form to
			// Encompass first, or eligibility gets evaluated against stale/blank loan data
			// (e.g. "Income Verification Type is not Inv - DSCR..." even when the user picked
			// DSCR here, because CX.IncomeVerifyType was never actually saved).
			await savePanelBProductInputs();

			// Saving can change Encompass's DTI (e.g. a new loan amount changes the payment), so
			// read 742 again and price with the value Encompass now has.
			const searchInputs = isDSCRstyle(incomeDocumentationType)
				? piFormState
				: { ...piFormState, debtToIncomeRatio: await readEncompassDti() };

			const loanDetails = await getLoan({ loanid: loanGuid }).unwrap();

			// Optimal Blue's own enums, as the Quick Pricer sends them; see buildEligibleSearchFormData.
			const formData = buildEligibleSearchFormData({
				s: searchInputs,
				loanGuid,
				loanNumber: loanDetails?.response?.loanNumber,
				user: effectiveUserWithChannel,
				selectedOrganization,
			});

			const eligibleProductList = await getEligibleProducts({
				formData,
				selectedOrganization,
				userDetails,
			}).unwrap();
			setEligibleProducts(eligibleProductList);

			// Only advance to Panel C once the search actually succeeded — advancing on
			// failure left the user stuck on an empty/broken Panel C with no products to
			// select and no visible error (panelBError only renders inside Panel B, which
			// this used to force-close).
			setPanelBStatus("complete");
			setPanelBOpen(false);
			setPanelCStatus("active");
			setPanelCOpen(true);
		} catch (err) {
			// Optimal Blue's transient 409 "Push is in queue." is not a loan conflict.
			if (isObPushInQueue(err)) {
				handleOpenCommonErrorModal("OB_PUSH_IN_QUEUE");
				return;
			}
			if (showConflictPopup(err)) return;
			setPanelBError(err?.data?.detail || "Unable to fetch eligible programs. Please try again.");
		} finally {
			setSearchingPrograms(false);
		}
	};

	// Save-on-leave: fires savePanelBProductInputs whenever Panel B closes, regardless of
	// validation — mirrors Dashboard's autosaveSection "always persist on navigate, even
	// if invalid" pattern. Redundant with handleSearchPrograms' own explicit save when
	// Panel B closes via "Search programs" (harmless/idempotent, same values) — this is
	// what actually covers the gap: the broker navigating away before ever clicking it.
	const panelBOpenRef = useRef(panelBOpen);
	useEffect(() => {
		// No Encompass writes while AURA runs.
		if (panelBOpenRef.current && !panelBOpen && !editLocked) {
			savePanelBProductInputs().catch((err) => {
				// Otherwise silent — background autosave — but a 409 is always shown.
				showConflictPopup(err);
			});
		}
		panelBOpenRef.current = panelBOpen;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [panelBOpen]);

	// Program the selected rate row belongs to — captured at selection time, since
	// expandedProduct can change (collapse/expand another product) before the click.
	const handleSelectRow = (row) => {
		setSelectedRow(row);
		setSelectedRowProductId(Object.keys(row || {}).length > 0 ? expandedProduct : "");
	};

	// Encompass loan number for OB registration — Redux first, live loan as fallback.
	const resolveLoanNumber = async () => {
		if (loanNumber) return loanNumber;
		const loanDetails = await getLoan({ loanid: loanGuid }).unwrap();
		return loanDetails?.response?.loanNumber || "";
	};

	// Old (pre-Sapiens) path — product family not configured for AUS. The conditions
	// shown are still the loan's real ones (no mock data).
	const applyFallbackAuraResult = () => {
		setAuraStatus("eligible");
		dispatch(setAuraResult("eligible"));
	};

	// AURA Run Process step 7b: CX.WRE.LASTRUN unchanged on two reads (every 15 s, at most
	// 10 min). Never rejects: a read error resolves { status: "error" }.
	const pollRulesEngine = (options = {}) =>
		pollRulesEngineUntilSettled({
			fetchLastRun: () => getRulesEngineStatus(loanGuid).unwrap(),
			isCancelled: () => !isMountedRef.current,
			...options,
		}).catch(() => ({ status: "error" }));

	// LC-696: AURA messages + documentation conditions. With settle, waits for the Workflow
	// Rules engine first (PathA-5.10); rulesEnginePoll is a 7b poll already running alongside
	// 7a. Otherwise a single read (e.g. after an upload). Resolves the 7b status, if any.
	const loadAuraConditions = async ({ settle = false, rulesEnginePoll } = {}) => {
		if (!isRealLoanGuid(loanGuid)) return undefined;
		let rulesEngineStatus;
		if (settle || rulesEnginePoll) {
			// If the engine doesn't settle — or can't be read — the conditions are still
			// shown, with the "may still be loading" notice (LC-AURA-005).
			const rulesEngine = await (rulesEnginePoll || pollRulesEngine());
			if (rulesEngine.status === "cancelled" || !isMountedRef.current) return rulesEngine.status;
			rulesEngineStatus = rulesEngine.status;
			setRulesEngineIncomplete(rulesEngine.status !== "done");
		}
		try {
			const value = await getAuraConditions(loanGuid).unwrap();
			if (isMountedRef.current && value) setAuraConditions(value);
		} catch (err) {
			handleOpenCommonErrorModal("AURA_CONDITIONS_ERROR");
		}
		return rulesEngineStatus;
	};

	// "Refresh conditions" on the LC-AURA-005 notice: check the rules engine again, then re-read.
	const handleRefreshConditions = async () => {
		setRefreshingConditions(true);
		try {
			await loadAuraConditions({ settle: true });
		} finally {
			if (isMountedRef.current) setRefreshingConditions(false);
		}
	};

	const openPanelD = () => {
		setPanelCStatus("complete");
		setPanelCOpen(false);
		setPanelDStatus("active");
		setPanelDOpen(true);
	};

	// Sapiens failed in the background (or "In Progress" went stale) — back to Step C
	// so the broker can run AURA again.
	const reopenPanelC = () => {
		setPanelDOpen(false);
		setPanelDStatus("pending");
		setPanelCStatus("active");
		setPanelCOpen(true);
	};

	// AURA Run Process steps 7a + 7b (rev. 10/21/2026), in parallel: CX.AUS.RESULT until
	// Sapiens finishes (7a) and CX.WRE.LASTRUN until the Workflow Rules engine settles (7b).
	// Once both are done: the result, elapsed time (step 8), the findings PDF and the conditions.
	// 7a without a result in 10 minutes = the run failed (AURA_NO_RESULT, back to Step C).
	const pollAuraStatus = async (startedAt) => {
		setAuraPending(false);
		setAuraEvaluating(true);
		setRulesEngineIncomplete(false);
		openPanelD();
		// 7b only counts two matching reads once 7a has its result; it stops if 7a fails.
		let resultReady = false;
		let resultFailed = false;
		const rulesEnginePoll = pollRulesEngine({
			canSettle: () => resultReady,
			isCancelled: () => !isMountedRef.current || resultFailed,
		});
		try {
			const outcome = await pollUntil({
				fetchStatus: () => getAuraStatus(loanGuid).unwrap(),
				isDone: isAuraStatusDone,
				intervalMs: AURA_POLL_INTERVAL_MS,
				timeoutMs: AURA_POLL_TIMEOUT_MS,
				isCancelled: () => !isMountedRef.current,
			});
			if (outcome.status === "cancelled") return;
			const status = outcome.value;
			// No result within 10 minutes (a final result whose findings PDF is still pending
			// is shown, without the PDF button).
			if (outcome.status === "timeout" && !status?.completed) {
				resultFailed = true;
				dispatch(setQualifyInputs({ auraRunStartedAt: null }));
				setAuraEvaluating(false);
				reopenPanelC();
				handleOpenCommonErrorModal("AURA_NO_RESULT");
				return;
			}
			dispatch(setQualifyInputs({ auraRunStartedAt: null }));
			if (status?.failed) {
				resultFailed = true;
				setAuraEvaluating(false);
				reopenPanelC();
				handleOpenCommonErrorModal(status?.stale ? "AURA_STALE" : "AURA_SAPIENS_UNAVAILABLE");
				return;
			}
			resultReady = true;
			const normalized = normalizeAuraResult(status?.result);
			setAuraRawResult(status?.result || "");
			setAuraFindings(
				status?.attachmentId
					? { attachmentId: status.attachmentId, title: status.attachmentTitle }
					: null
			);
			setAuraStatus(normalized);
			dispatch(setAuraResult(normalized));
			// Hold the progress state until 7b is done too, then read the conditions once (PathA-5.10).
			const rulesEngineStatus = await loadAuraConditions({ rulesEnginePoll });
			if (rulesEngineStatus === "cancelled" || !isMountedRef.current) return;
			// Step 8: end time + duration once both 7a and 7b are done.
			const elapsedMs = startedAt ? Date.now() - startedAt : null;
			setAuraElapsed(elapsedMs !== null ? formatElapsed(elapsedMs) : "");
			logRumEvent("aura_run_completed", {
				loanGuid,
				durationSeconds: elapsedMs !== null ? Math.round(elapsedMs / 1000) : null,
				result: normalized,
				rulesEngineSettled: rulesEngineStatus === "done",
			});
		} catch (err) {
			resultFailed = true;
			setAuraPending(true);
			handleOpenCommonErrorModal("AURA_STATUS_ERROR");
		} finally {
			if (isMountedRef.current) setAuraEvaluating(false);
		}
	};

	// Panel C — register the selected program in Optimal Blue (same calls as Add New
	// Loan → Program & Pricing), then run Sapiens Decision Assist and poll for the
	// result. Errors keep the user on Step C instead of silently advancing.
	// One AURA run at a time: the backend call is synchronous (up to 5 min), so a second
	// click (or Retry) while it's in flight must not fire another Sapiens event.
	const auraRunInFlightRef = useRef(false);
	const handleRunAura = async () => {
		if (Object.keys(selectedRow || {}).length === 0) return;
		if (auraRunInFlightRef.current || editLocked) return;
		auraRunInFlightRef.current = true;
		const productId = selectedRowProductId || expandedProduct;
		const parentProduct = eligibleProducts?.data?.products?.find(
			(p) => p?.productId === productId
		);
		const prog = {
			...selectedRow,
			id: productId,
			name: parentProduct?.productName || selectedProduct || "",
		};
		const startedAt = Date.now();
		const obRegisteredKey = buildObRegistrationKey(productId, selectedRow);
		setAuraNotConfigured(false);
		setAuraPending(false);
		setAuraFindings(null);
		setAuraElapsed("");
		setRunningAura(true);

		// Same product + rate row already registered (e.g. re-run after a Sapiens
		// failure) — skip the second Optimal Blue registration.
		if (savedInputs.obRegisteredKey !== obRegisteredKey) {
			try {
				setProcessingText("Registering program with Optimal Blue…");
				await registerObProgram({
					eligibleData: eligibleProducts?.data,
					productId,
					selectedRow,
					loanGuid,
					loanNumber: await resolveLoanNumber(),
					sourceArea: "Current Loan - Qualify AURA",
				});
			} catch (err) {
				auraRunInFlightRef.current = false;
				setRunningAura(false);
				// A real 409 (loan changed / AURA running) keeps its popup; Optimal Blue's
				// "Push is in queue." (retried already) shows OB_PUSH_IN_QUEUE instead.
				if (err?.errorKey !== "OB_PUSH_IN_QUEUE" && showConflictPopup(err?.cause || err)) return;
				handleOpenCommonErrorModal(err?.errorKey || "PRODUCT_UPDATE_FAILED_SYSTEM");
				return;
			}
		}
		// Panel D's "registered" completion flag is set only on its Continue.
		dispatch(setSelectedProgram(prog));
		dispatch(setQualifyInputs({ obRegisteredKey }));

		await runSapiensDecisionAssist(productId, startedAt);
	};

	// One status read: true only while CX.AUS.RESULT is "In Progress" (a final value may be
	// an earlier run's result).
	const isAuraRunInProgress = async () => {
		try {
			const status = await getAuraStatus(loanGuid).unwrap();
			return !!status && !status.completed;
		} catch (err) {
			return false;
		}
	};

	// Sapiens half of Step C: set CX.AUS.* + fire the decision event, then poll.
	// The caller has already claimed auraRunInFlightRef; it is released when this settles.
	const runSapiensDecisionAssist = async (productId, startedAt) => {
		try {
			setRunningAura(false);
			setAuraConditions(null);
			setRulesEngineIncomplete(false);
			// PathA-5.10: "AURA is evaluating your loan" from the run itself; no time limit shown.
			setAuraEvaluating(true);
			openPanelD();
			let started;
			try {
				// Synchronous: the backend waits for Sapiens to accept the event (CP-548: up to 5 min).
				started = await runAura({ loanGuid, programId: productId }).unwrap();
			} catch (err) {
				// Gateway / connection gave up while the backend may still be waiting on Sapiens:
				// if the loan shows AURA in progress, the run was accepted — keep checking it.
				if (isGatewayOrNetworkError(err) && (await isAuraRunInProgress())) {
					dispatch(setQualifyInputs({ auraRunStartedAt: startedAt }));
					await pollAuraStatus(startedAt);
					return;
				}
				setAuraEvaluating(false);
				reopenPanelC();
				// The failed step (error.reason) picks the message. Otherwise a 409 with the
				// backend's message (AURA running / loan changed) shows that message; a bare 409
				// keeps the "Loan is in use" text.
				const reasonKey = auraRunErrorKey(err);
				if (reasonKey) {
					handleOpenCommonErrorModal(reasonKey);
				} else if (!(err?.data?.error && showConflictPopup(err))) {
					handleOpenCommonErrorModal(
						isConflictError(err) ? "AURA_LOAN_LOCKED" : "AURA_SAPIENS_UNAVAILABLE"
					);
				}
				return;
			}

			if (started?.status === "not_configured") {
				setAuraNotConfigured(true);
				applyFallbackAuraResult();
				await loadAuraConditions();
				setAuraEvaluating(false);
				return;
			}

			dispatch(setQualifyInputs({ auraRunStartedAt: startedAt }));
			await pollAuraStatus(startedAt);
		} finally {
			auraRunInFlightRef.current = false;
		}
	};

	// A run started on an earlier visit (navigated away mid-poll) — resume polling.
	const resumePollRef = useRef(false);
	useEffect(() => {
		if (resumePollRef.current) return;
		if (!savedInputs.auraRunStartedAt || savedAura || !isRealLoanGuid(loanGuid)) return;
		resumePollRef.current = true;
		pollAuraStatus(savedInputs.auraRunStartedAt);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid]);

	// The raw AURA value and findings PDF live only in this page's state — one status
	// read (no polling) brings them back on a later visit.
	const restoreAuraResultDetails = async () => {
		try {
			const status = await getAuraStatus(loanGuid).unwrap();
			if (!isMountedRef.current || !status?.completed) return;
			if (status.result) setAuraRawResult(status.result);
			if (status.attachmentId) {
				setAuraFindings({ attachmentId: status.attachmentId, title: status.attachmentTitle });
			}
		} catch (err) {
			handleOpenCommonErrorModal("AURA_STATUS_ERROR");
		}
	};

	// Returning to a finished Step D — restore the result details and read the conditions once.
	const conditionsLoadedRef = useRef(false);
	useEffect(() => {
		if (conditionsLoadedRef.current || !savedAura || savedInputs.auraRunStartedAt) return;
		if (!isRealLoanGuid(loanGuid)) return;
		conditionsLoadedRef.current = true;
		restoreAuraResultDetails();
		loadAuraConditions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanGuid]);

	const handleRetryAura = () => {
		if (auraRunInFlightRef.current || editLocked) return;
		auraRunInFlightRef.current = true;
		runSapiensDecisionAssist(selectedProgram?.id || selectedRowProductId || expandedProduct, Date.now());
	};

	// Fetches the Sapiens findings PDF from the Encompass eFolder. Returns a blob URL
	// when the attachment is inline (b64), otherwise the eFolder's own URL.
	const fetchFindingsPdf = async () => {
		const formData = new FormData();
		formData.append("loan_guid", loanGuid);
		formData.append("attachment_ids", auraFindings.attachmentId);
		const response = await downloadAttachment({ formData }).unwrap();
		const attachment = response?.response?.attachments?.[0];
		const b64 = attachment?.b64;
		if (b64) {
			const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
			const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
			return { url, isBlob: true };
		}
		// URL-type eFolder attachment (no inline b64) — same fallback as Loan Documents.
		const viewUrl = attachment?.url || attachment?.originalUrls?.[0];
		if (!viewUrl) throw new Error("empty attachment");
		return { url: viewUrl, isBlob: false };
	};

	// Opens the Sapiens findings PDF in a new tab.
	const handleOpenFindingsPdf = async () => {
		if (!auraFindings?.attachmentId) return;
		setProcessingText("Opening AURA findings…");
		setRunningAura(true);
		try {
			const { url, isBlob } = await fetchFindingsPdf();
			window.open(url, "_blank", "noopener");
			if (isBlob) setTimeout(() => URL.revokeObjectURL(url), 60000);
		} catch (err) {
			handleOpenCommonErrorModal("AURA_FINDINGS_PDF_ERROR");
		} finally {
			setRunningAura(false);
		}
	};

	// Saves the Sapiens findings PDF to the user's device.
	const handleDownloadFindingsPdf = async () => {
		if (!auraFindings?.attachmentId) return;
		setProcessingText("Downloading AURA findings…");
		setRunningAura(true);
		try {
			const { url, isBlob } = await fetchFindingsPdf();
			if (!isBlob) {
				// The download attribute is ignored for cross-origin URLs — open it instead.
				window.open(url, "_blank", "noopener");
				return;
			}
			const link = document.createElement("a");
			link.href = url;
			link.download = `AURA-Findings-${loanGuid}.pdf`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			setTimeout(() => URL.revokeObjectURL(url), 60000);
		} catch (err) {
			handleOpenCommonErrorModal("AURA_FINDINGS_PDF_ERROR");
		} finally {
			setRunningAura(false);
		}
	};

	// Panel D continue — the program is already registered with Optimal Blue in Step C;
	// the Float/Lock decision itself happens once, later, on Submit's pre-submission step.
	const handleRegisterAndContinue = async () => {
		// Before the Dashboard reads it on mount.
		await ensureCreditProvidedReissue();
		dispatch(setQualifyInputs({ registered: true }));
		// Straight to Submit when every Dashboard section is already complete.
		const stage = getPostQualifyStage(sectionStatuses);
		dispatch(setCurrentStage(stage));
		navigate(`/current-loan/${stage}`);
	};

	// LC-696: the result shows only once AURA and its conditions have settled.
	const showAuraResult = !auraPending && !auraEvaluating;

	// AURA's messages, then the documentation conditions — on every result (PathA-5.11, 5.05).
	const auraMessages = <AuraEligibilityMessages messages={auraConditions?.messages} />;
	const auraConditionList = (
		<Box mb={2}>
			{rulesEngineIncomplete && (
				<Box mb={2}>
					<CustomAlert
						severity="warning"
						subject={VERBIAGE_CONSTANTS.AURA_CONDITIONS_STILL_LOADING_TITLE}
						details={VERBIAGE_CONSTANTS.AURA_CONDITIONS_STILL_LOADING_DETAIL}
					/>
					<SecondaryButton
						text={VERBIAGE_CONSTANTS.AURA_REFRESH_CONDITIONS}
						onClick={handleRefreshConditions}
						disabled={refreshingConditions}
						extraClass={styles.mt}
					/>
				</Box>
			)}
			<AuraConditionList
				conditions={auraConditions}
				loanGuid={loanGuid}
				onRefresh={() => loadAuraConditions()}
				onError={handleOpenCommonErrorModal}
				disabled={editLocked}
			/>
		</Box>
	);
	const auraMessagesAndConditions = (
		<>
			{auraMessages}
			{auraConditionList}
		</>
	);

	// "Pass / Eligible" / "Refer / Ineligible — Requires Exception" with AURA's raw value beneath.
	const auraResultValue = auraRawResult && (
		<Body3
			text={`${VERBIAGE_CONSTANTS.AURA_RESULT_PREFIX} ${auraRawResult}`}
			extraClass={styles.auraResultValue}
		/>
	);

	const panelASubTitle = creditResult
		? `FICO ${creditResult.fico} · ${creditResult.tradelines} tradelines · ${creditResult.housing}`
		: undefined;

	const selectedProductParent = eligibleProducts?.data?.products?.find(
		(p) => p?.productId === expandedProduct
	);
	// CLEARpath v3.1 Step D header subtitle, once the result is showing.
	const panelDSubTitle = !showAuraResult
		? undefined
		: auraStatus === "eligible"
			? VERBIAGE_CONSTANTS.AURA_STEP_D_SUB_ELIGIBLE
			: auraStatus === "ineligible"
				? VERBIAGE_CONSTANTS.AURA_STEP_D_SUB_INELIGIBLE
				: undefined;

	const panelCSubTitle =
		Object.keys(selectedRow).length > 0 && selectedProductParent
			? selectedProductParent.productName
			: undefined;

	return (
		<CustomLayout>
			<LoaderModal open={searchingPrograms} />
			<ProcessingModal open={runningCredit} text="Running credit re-issue…" />
			<ProcessingModal open={runningAura} text={processingText} subText={processingText} />
			<CommanErrorModal
				isOpen={commonErrorModalVisible}
				onClose={handleCloseCommonErrorModal}
				title={commonErrorData?.title}
				message={commonErrorData?.message}
				errorCode={commonErrorData?.code}
			/>
			<LoaderModal open={readingDti} />
			{/* PathA-2.02 / 2.17: confirm before any re-issue call; Cancel leaves the loan unchanged. */}
			<ConfirmationModal
				open={reissueConfirmPair !== null}
				text={
					reissueConfirmPair !== null && isRepeatReissue(reissueConfirmPair)
						? `${VERBIAGE_CONSTANTS.CREDIT_REISSUE_REPEAT_TITLE}. ${VERBIAGE_CONSTANTS.CREDIT_REISSUE_REPEAT}`
						: `${VERBIAGE_CONSTANTS.CREDIT_REISSUE_CONFIRM_TITLE}. ${VERBIAGE_CONSTANTS.CREDIT_REISSUE_CONFIRM}`
				}
				confirmText={VERBIAGE_CONSTANTS.CREDIT_REISSUE_CONFIRM_BUTTON}
				handleConfirm={() => {
					const pairIndex = reissueConfirmPair;
					setReissueConfirmPair(null);
					handleReissuePair(pairIndex);
				}}
				closeText="Cancel"
				handleClose={() => setReissueConfirmPair(null)}
			/>
			{/* PathA-2.04 / 2.05: post-re-issue prompt with the deep-link to Assets & liabilities. */}
			<ConfirmationModal
				open={reissueCompleteOpen}
				text={`${VERBIAGE_CONSTANTS.CREDIT_REISSUE_COMPLETE_TITLE}. ${VERBIAGE_CONSTANTS.CREDIT_REISSUE_COMPLETE}`}
				confirmText={VERBIAGE_CONSTANTS.CREDIT_REISSUE_REVIEW}
				handleConfirm={handleReviewLiabilities}
				closeText={VERBIAGE_CONSTANTS.CREDIT_REISSUE_STAY}
				handleClose={() => setReissueCompleteOpen(false)}
			/>
			{/* Same disclaimer as the wizard when continuing with pairs not re-issued */}
			<ConfirmationModal
				open={reissueDisclaimerOpen}
				text={VERBIAGE_CONSTANTS.CREDIT_REISSUE_DISCLAIMER}
				confirmText="Reissue credit to all borrowers"
				handleConfirm={() => {
					setReissueDisclaimerOpen(false);
					const nextPair = nextPairToReissue(reissueStatusesRef.current, creditPairCount, creditTab);
					if (nextPair >= 0) setCreditTab(nextPair);
				}}
				closeText="Continue"
				handleClose={() => {
					setReissueDisclaimerOpen(false);
					completePanelA();
				}}
			/>
			<ExceptionRequestModal
				open={exceptionModalOpen}
				loanGuid={loanGuid}
				disabled={editLocked}
				handleClose={() => setExceptionModalOpen(false)}
				onSubmitted={() => dispatch(setExceptionStatus("pending"))}
				onSubmit={(dest) => {
					setExceptionModalOpen(false);
					if (dest === "submit") {
						navigate("/current-loan/submit");
					}
				}}
			/>

			<Box className={styles.pageRoot}>
				<LoanInfoCard loanId={loanGuid} />
				<AuraInProgressBanner show={editLocked} />
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
					<Stack direction="column">
						<Heading5 text="Step 3: Qualify" extraClass={styles.pageTitle} />
						<Body3 text="Complete qualification sub-steps" extraClass={styles.subTitle} />
					</Stack>
					<Stack direction="row" alignItems="center" gap={1}>
						<Chip label="QUALIFICATION ENGINE" size="small" className={styles.qeLabel} />
						<Chip label="AURA path" size="small" className={styles.auraBadge} />
						<Box
							component="span"
							className={styles.switchLink}
							onClick={() => navigate("/current-loan/qualify/path-select")}
						>
							Switch path →
						</Box>
					</Stack>
				</Stack>

				{/* Horizontal stepper — labels/subtitles match the CLEARpath prototype exactly */}
				<Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={3} className={styles.stepper}>
					<StepLabel
						letter="A"
						label="Credit re-issue"
						sub="Auto-fills credit profile"
						status={panelAStatus === "complete" ? "done" : panelAStatus === "active" ? "active" : "pending"}
					/>
					<StepLabel
						letter="B"
						label="Product inputs"
						sub="Optimal Blue parameters"
						status={panelBStatus === "complete" ? "done" : panelBStatus === "active" ? "active" : "pending"}
					/>
					<StepLabel
						letter="C"
						label="Available programs and pricing"
						sub="Select → AURA fires"
						status={panelCStatus === "complete" ? "done" : panelCStatus === "active" ? "active" : "pending"}
					/>
					<StepLabel
						letter="D"
						label="AURA result and conditions"
						sub="Result, messages & conditions"
						status={panelDStatus === "complete" ? "done" : panelDStatus === "active" ? "active" : "pending"}
						isLast
					/>
				</Stack>

				{/* Accordion A — Credit Re-issue */}
				<Box mb={2}>
					<QualifyAccordion
						stepLabel="A"
						title="Credit re-issue"
						subTitle={panelASubTitle}
						status={inputStepStatus(panelAStatus)}
						isOpen={panelAOpen}
						onToggle={() => setPanelAOpen((v) => !v)}
					>
						{creditResult && (
							<Box className={styles.creditResultCard} mb={2}>
								<Stack direction="row" gap={3} flexWrap="wrap">
									<Stack direction="column">
										<span className={styles.ficoLabel}>Qualifying FICO</span>
										<span className={styles.ficoValue}>{creditResult.fico}</span>
									</Stack>
									<ReadOnlyField label="Active tradelines" value={String(creditResult.tradelines)} />
									<ReadOnlyField label="Housing history" value={creditResult.housing} />
									<ReadOnlyField label="Housing event" value={creditResult.event} />
								</Stack>
							</Box>
						)}

						{/* One tab per borrower pair — same per-pair credit re-issue as the Add New Loan wizard */}
						{creditPairCount > 1 && (
							<Tabs
								value={creditTab}
								onChange={(event, value) => setCreditTab(value)}
								variant="scrollable"
								aria-label="Borrower pairs"
								className={styles.creditPairTabs}
							>
								{Array.from({ length: creditPairCount }, (_, pairIndex) => (
									<Tab
										key={pairIndex}
										label={`Borrower pair ${pairIndex + 1}${
											reissueStatuses[pairIndex] === REISSUE_STATUS.SUCCESS ? " ✓" : ""
										}`}
										id={`qualify-aura-credit-tab-${pairIndex}`}
										aria-controls={`qualify-aura-credit-tabpanel-${pairIndex}`}
									/>
								))}
							</Tabs>
						)}

						{Array.from({ length: creditPairCount }, (_, pairIndex) => {
							const borrower = creditPairPeople[pairIndex * 2] || {};
							const coBorrower = creditPairPeople[pairIndex * 2 + 1] || {};
							const pairStatus = reissueStatuses[pairIndex];
							return (
								<TabPanel
									key={pairIndex}
									value={creditPairCount > 1 ? creditTab : 0}
									index={pairIndex}
									id={`qualify-aura-credit-tabpanel-${pairIndex}`}
									aria-labelledby={`qualify-aura-credit-tab-${pairIndex}`}
								>
									<Heading5
										text={pairIndex === 0 ? "Borrower data from 3.4 XML" : `Borrower pair ${pairIndex + 1}`}
										extraClass={styles.sectionLabel}
									/>
									{pairIndex === 0 ? (
										<Stack direction="row" gap={2} flexWrap="wrap" mb={2}>
											<ReadOnlyField label="First name" value={displayData.firstName} tag={displayData.firstName ? "XML" : undefined} />
											<ReadOnlyField label="Last name" value={displayData.lastName} tag={displayData.lastName ? "XML" : undefined} />
											<ReadOnlyField label="Date of birth" value={displayData.dateOfBirth} tag={displayData.dateOfBirth ? "XML" : undefined} />
											<ReadOnlyField label="SSN" value={displayData.ssn} tag={displayData.ssn ? "XML" : undefined} />
										</Stack>
									) : (
										<Stack direction="row" gap={2} flexWrap="wrap" mb={2}>
											<ReadOnlyField
												label="Borrower"
												value={[borrower.firstName, borrower.lastName].filter(Boolean).join(" ")}
											/>
											<ReadOnlyField
												label="Co-borrower"
												value={[coBorrower.firstName, coBorrower.lastName].filter(Boolean).join(" ")}
											/>
										</Stack>
									)}

									<CreditCredentialsInformation
										creditCredentialsDataIndex={pairIndex}
										creditCredentialsData={creditCredentialsData}
										setCreditCredentialsData={setCreditCredentialsData}
										error={creditErrorPair === pairIndex}
										creditVendorsData={creditVendorsData}
										loanApplicationData={creditPairPeople}
										disabled={runningCredit}
										showRequiredMarks
										productCodeInfo={VERBIAGE_CONSTANTS.CREDIT_REPORT_PRODUCT_CODE_INFO}
									/>

									{pairStatus === REISSUE_STATUS.SUCCESS && (
										<CustomAlert
											subject="Credit re-issued"
											details={`Credit was re-issued for borrower pair ${pairIndex + 1}.`}
											severity="success"
											extraClass={styles.alertSpacing}
										/>
									)}
									{panelAError && creditErrorPair === pairIndex && <RequiredError text={panelAError} />}
									{panelAError && creditErrorPair === -1 && <RequiredError text={panelAError} />}
									{pollTimedOut && lastPollArgsRef.current?.pollArgs?.pairIndex === pairIndex && (
										<CustomAlert
											subject="Still processing"
											details="The credit vendor hasn't finished processing this order yet. This can happen with some credit-vendor connections. You can keep waiting and check again, or continue and re-run credit re-issue later."
											severity="warning"
											extraClass={styles.alertSpacing}
										/>
									)}
									<Stack direction="row" gap={1.5} mt={2}>
										<PrimaryButton
											text={
												creditPairCount > 1
													? `Run credit re-issue for pair ${pairIndex + 1} →`
													: "Run Credit Re-issue →"
											}
											onClick={() => setReissueConfirmPair(pairIndex)}
											disabled={runningCredit || editLocked}
										/>
										{pollTimedOut && lastPollArgsRef.current?.pollArgs?.pairIndex === pairIndex && (
											<SecondaryButton text="Check status again" onClick={handleCheckCreditStatusAgain} />
										)}
										{reissueStatuses.some(Boolean) && (
											<SecondaryButton
												text="Continue →"
												onClick={handleContinueFromCredit}
												disabled={runningCredit}
											/>
										)}
									</Stack>
								</TabPanel>
							);
						})}
					</QualifyAccordion>
				</Box>
				{/* Accordion B — Product Inputs */}
				<Box mb={2}>
					<QualifyAccordion
						stepLabel="B"
						title="Product & pricing inputs"
						status={inputStepStatus(panelBStatus)}
						isOpen={panelBOpen}
						onToggle={() => panelBStatus !== "pending" && setPanelBOpen((v) => !v)}
					>
						<CustomAlert
							subject="Source note"
							details="Fields tagged XML were pre-populated from your upload. Fields tagged Credit re-issue were auto-filled from Step A. Any fields left blank may also be pre-filled from a Quick Pricer search you've already run for this borrower."
							severity="info"
						/>

						<ProductInputsPanel
							formState={piFormState}
							setField={piSetField}
							errors={piErrors}
							submitted={piSubmitted}
						/>

						{panelBError && <RequiredError text={panelBError} />}
						<PrimaryButton text="Search programs →" onClick={handleSearchPrograms} disabled={editLocked} />
					</QualifyAccordion>
				</Box>
				{/* Accordion C — Available Programs */}
				<Box mb={2}>
					<QualifyAccordion
						stepLabel="C"
						title="Available programs"
						subTitle={panelCSubTitle}
						status={panelCStatus}
						isOpen={panelCOpen}
						onToggle={() => panelCStatus !== "pending" && setPanelCOpen((v) => !v)}
					>
						{/* Eligible program list only — no Ineligible tab (CLEARpath prototype v2.1 Step C) */}
						<CustomAlert
							subject="Indicative pricing"
							details="Showing available programs from Optimal Blue. Full rate and price confirmed after AURA runs in Step D."
							severity="info"
						/>
						{eligibleProducts?.data?.products?.length > 0 ? (
							<EligibleFilter
								eligibleProducts={eligibleProducts?.data?.products}
								expandedProduct={expandedProduct}
								toggleExpand={handleToggleExpand}
								eligibleProductDetails={eligibleProductDetails?.data}
								selectedRow={selectedRow}
								setSelectedRow={handleSelectRow}
								setSelectedProduct={setSelectedProduct}
								toggleDrawer={() => {}}
							/>
						) : (
							<EmptyTextContainer
								headerText="No products available"
								bodyText="No eligible programs were returned by Optimal Blue for the current inputs. Adjust your Product Inputs and search again."
							/>
						)}
						<PrimaryButton
							text="Select program & run AURA →"
							onClick={handleRunAura}
							disabled={Object.keys(selectedRow).length === 0 || runningAura || auraEvaluating || editLocked}
						/>
					</QualifyAccordion>
				</Box>
				{/* Accordion D — AURA Result */}
				<Box mb={3}>
					<QualifyAccordion
						stepLabel="D"
						title="AURA result and conditions"
						subTitle={panelDSubTitle}
						status={panelDStatus}
						isOpen={panelDOpen}
						onToggle={() => panelDStatus !== "pending" && setPanelDOpen((v) => !v)}
					>
						<RegisteredProgramCard program={selectedProgram} title="Your registered program & pricing" />

						{liabilitiesChangedSinceAura && (
							<Box mb={2}>
								<CustomAlert
									severity="warning"
									subject={VERBIAGE_CONSTANTS.LIABILITIES_CHANGED_AFTER_AURA}
								/>
							</Box>
						)}

						{auraNotConfigured && (
							<Box mb={2}>
								<CustomAlert
									subject="AURA is not configured for this product family"
									details="Automated Decision Assist isn't available for this loan's product family yet, so it continues on the standard path."
									severity="info"
								/>
							</Box>
						)}

						{auraEvaluating && (
							<AuraProgress detail={VERBIAGE_CONSTANTS.AURA_EVALUATING_DETAIL} />
						)}

						{auraPending && (
							<>
								<CustomAlert
									subject="AURA is still processing"
									details="The decision engine hasn't returned a result yet. Check again in a minute — you can also leave and re-open this loan later."
									severity="warning"
								/>
								<SecondaryButton
									text="Check again"
									onClick={() => pollAuraStatus(savedInputs.auraRunStartedAt || null)}
									extraClass={styles.mt}
								/>
							</>
						)}

						{showAuraResult && (auraElapsed || auraFindings) && (
							<Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
								<Body3 text={auraElapsed ? `AURA completed in ${auraElapsed}` : ""} />
								{auraFindings?.attachmentId && (
									<Stack direction="row" gap={1} flexWrap="wrap">
										<SecondaryButton
											text="View AURA findings (PDF)"
											onClick={handleOpenFindingsPdf}
										/>
										<SecondaryButton
											text="Download AURA findings (PDF)"
											onClick={handleDownloadFindingsPdf}
										/>
									</Stack>
								)}
							</Stack>
						)}

						{showAuraResult && auraStatus === "eligible" && (
							<>
								<Box className={styles.auraEligibleBar}>
									<CheckCircleOutlined sx={{ fontSize: 18, mr: 1 }} />
									{VERBIAGE_CONSTANTS.AURA_RESULT_ELIGIBLE}
								</Box>
								{auraResultValue}

								{/* PathA-5.03: fixed list, the same for every product; the PDF has the rules. */}
								<Heading5 text={VERBIAGE_CONSTANTS.AURA_EVALUATED_TITLE} extraClass={styles.sectionLabel} />
								<Box className={styles.rulesPassedGrid} mb={2}>
									{VERBIAGE_CONSTANTS.AURA_EVALUATED_CATEGORIES.map((category) => (
										<Stack key={category} direction="row" alignItems="center" gap={1} className={styles.rulesPassedItem}>
											<CheckCircleOutlined sx={{ fontSize: 16 }} className={styles.rulesPassedIcon} />
											<Body3 text={category} />
										</Stack>
									))}
								</Box>

								{auraMessagesAndConditions}

								{/* Rate/lock decisions are made once, later, on the Submit page's
								    pre-submission "Float & Submit" / "Request Lock & Submit" step —
								    not here, to avoid two divergent capture points for the same choice. */}
								<Box mt={2}>
									<PrimaryButton
										text="Continue →"
										onClick={handleRegisterAndContinue}
									/>
								</Box>
							</>
						)}

						{showAuraResult && auraStatus === "ineligible" && (
							<>
								<Box className={styles.auraIneligibleBar}>
									<CancelOutlined sx={{ fontSize: 18, mr: 1 }} />
									{VERBIAGE_CONSTANTS.AURA_RESULT_INELIGIBLE}
								</Box>
								{auraResultValue}

								{/* CLEARpath v3.1 unhappy path: messages, "prepare your file" callout,
								    conditions, then Back to programs / Submit exception request. */}
								<Box mt={2}>{auraMessages}</Box>
								<Box mb={2}>
									<CustomAlert
										severity="info"
										subject={VERBIAGE_CONSTANTS.AURA_PREPARE_FILE_TITLE}
										details={VERBIAGE_CONSTANTS.AURA_PREPARE_FILE_DETAIL}
									/>
								</Box>
								{auraConditionList}

								<Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} mt={2} flexWrap="wrap">
									<SecondaryButton
										text="← Back to programs"
										onClick={() => { setPanelCOpen(true); setPanelDOpen(false); }}
									/>
									<PrimaryButton
										text="Submit exception request →"
										onClick={() => setExceptionModalOpen(true)}
										disabled={editLocked}
										extraClass={styles.exceptionBtn}
									/>
								</Stack>
							</>
						)}

						{showAuraResult && auraStatus === "error" && (
							<>
								<CustomAlert
									subject="AURA error"
									details="The AURA eligibility engine returned an error. Please retry."
									severity="error"
								/>
								<SecondaryButton
									text="Retry AURA"
									onClick={handleRetryAura}
									disabled={editLocked}
									extraClass={styles.mt}
								/>
							</>
						)}

						{/* Fallback for any status value other than the 3 known ones above — without
						    this, an unrecognized status silently rendered nothing but the program
						    card, leaving the user stuck with no way to move on. */}
						{showAuraResult &&
							auraStatus &&
							auraStatus !== "eligible" &&
							auraStatus !== "ineligible" &&
							auraStatus !== "error" && (
								<>
									<CustomAlert
										subject="Unexpected AURA result"
										details={`AURA returned an unrecognized status ("${auraStatus}"). You can update your inputs and try again, or submit an exception request.`}
										severity="warning"
									/>
									<Stack direction="row" gap={2} mt={2} flexWrap="wrap">
										<Box className={styles.actionBox}>
											<Heading5 text="Option A — Update inputs" />
											<Body3 text="Adjust product inputs or program selection and try again." />
											<SecondaryButton
												text="← Update inputs"
												onClick={() => { setPanelBOpen(true); setPanelCOpen(false); setPanelDOpen(false); }}
											/>
										</Box>
										<Box className={styles.actionBox}>
											<Heading5 text="Option B — Submit exception" />
											<Body3 text="Submit an exception request for Lock Desk review." />
											<PrimaryButton
												text="Submit exception request →"
												onClick={() => setExceptionModalOpen(true)}
												disabled={editLocked}
												extraClass={styles.exceptionBtn}
											/>
										</Box>
									</Stack>
								</>
							)}
					</QualifyAccordion>
				</Box>

				<Box className={styles.disclaimer}>
					Not a commitment to lend. All loans subject to underwriting approval.
				</Box>
			</Box>
		</CustomLayout>
	);
};

// Render errors show an error popup instead of a blank page.
const QualifyAuraPageWithBoundary = () => (
	<PageErrorBoundary errorKey="AURA_PAGE_ERROR">
		<QualifyAuraPage />
	</PageErrorBoundary>
);

export default QualifyAuraPageWithBoundary;
